/**
 * MEXC P2P Adapter — normalizes all MEXC API calls to the domain model.
 *
 * Correct endpoint: GET https://p2p.mexc.com/api/market
 * Response format: { code: 0, message: "success", data: [Offer[]] } (flat array)
 *
 * MEXC Offer fields → Domain:
 *   id → advNo
 *   price → price (string → number)
 *   availableQuantity → surplusAmount (number)
 *   coinName → asset
 *   tradeType → tradeType ("BUY" | "SELL")
 *   merchant.nickName → advertiser.nickName
 *   merchantStatistics.goodRate → advertiser.monthFinishRate (parse "98.50%" → 0.985)
 *   memberId → advertiser.userNo
 *   minTradeLimit → minSingleTransAmount
 *   maxTradeLimit → maxSingleTransAmount
 */

import axios from "axios";
import { ExchangeAdapter } from "./types";
import { P2PAd, PriceSignal, SearchParams, SearchResult, P2PAdDetail } from "./domain";

// ─── MEXC P2P API endpoint ──────────────────────────────────────────────
const MEXC_P2P_HOST = "p2p.mexc.com";

// CORS proxies that work with MEXC
const MEXC_CORS_PROXIES = [
  "https://freehost-2.lehongthai.vip/cors-anywhere.php",
  "https://host-1.lehongthai.vip/cors-anywhere.php",
];

// USDT coin ID for MEXC P2P
const MEXC_USDT_COIN_ID = "128f589271cb4951b03e71e6323eb7be";

// ─── VIP merchant rules (MEXC-specific) ─────────────────────────────────
interface VipRule {
  match: (ad: P2PAd) => boolean;
  className: string;
  minAccessLevel?: number;
}

const VIP_RULES: VipRule[] = [
  // MEXC promotion zone (orderTag id 2 = "精选广告", 3 = "快速放币")
  { match: ad => !!ad.promotionZone, className: "!text-white !bg-slate-600 !opacity-100 relative before:absolute before:inset-0 before:border-2 before:border-yellow-400 before:rounded-md before:animate-[pulse_2s_ease-in-out_infinite]", minAccessLevel: 0 },
];  // MEXC — rules will be added when docs are available

// ─── Helper: pick random host ───────────────────────────────────────────
function randomHost<T>(hosts: T[]): T {
  return hosts[Math.floor(Math.random() * hosts.length)];
}

// ─── Helper: detect mobile from User-Agent ──────────────────────────────
function isMobileFromUA(): boolean {
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

// ─── Parse MEXC percentage string like "98.50%" → number (0.9850) ───────
function parsePercent(raw: string): number {
  if (!raw) return 0;
  const num = parseFloat(raw.replace("%", ""));
  if (isNaN(num)) return 0;
  // MEXC goodRate is like "98.50" meaning 98.50%
  return num / 100;
}

// ─── Detect MEXC promotion zone from orderTag (id 2 = "精选广告", 3 = "快速放币") ─
function isPromotionZone(item: any): boolean {
  const tag = item.orderTag;
  if (!tag) return false;
  const id = Number(tag.id ?? 0);
  return id === 2 || id === 3;
}

// ─── Normalize a MEXC Offer to P2PAd ────────────────────────────────────
function normalizeOffer(item: any): P2PAd {
  const merchant = item.merchant || {};
  const stats = item.merchantStatistics || {};

  return {
    advNo: String(item.id ?? ""),
    tradeType: (item.tradeType === "BUY" || item.tradeType === "B" || item.tradeType === 0) ? "BUY" : "SELL",
    asset: (item.coinName ?? item.asset ?? "USDT").toUpperCase(),
    price: String(Number(item.price ?? 0)),
    surplusAmount: Number(item.availableQuantity ?? item.surplusAmount ?? 0),
    tradableQuantity: String(item.availableQuantity ?? ""),
    maxSingleTransAmount: Number(item.maxTradeLimit ?? 0),
    minSingleTransAmount: Number(item.minTradeLimit ?? 0),
    dynamicMaxSingleTransAmount: Number(item.maxTradeLimit ?? 0),
    commissionRate: 0,
    payTimeLimit: 15,
    classify: item.classify || undefined,
    promotionZone: isPromotionZone(item),
    takerAdditionalKycRequired: Number(item.kycLevel ?? 0),
    tradeMethods: [],
    advertiser: {
      userNo: String(merchant.memberId ?? ""),
      nickName: merchant.nickName ?? merchant.name ?? "",
      userType: normalizeUserType(merchant.merchantType ?? "USER"),
      monthFinishRate: parsePercent(stats.goodRate ?? ""),
      monthOrderCount: Number(stats.totalCount ?? 0),
      positiveRate: parsePercent(stats.goodRate ?? "0%"),
      activeTimeInSecond: merchant.lastOnlineTime
        ? Math.floor((Date.now() / 1000) - (merchant.lastOnlineTime > 1e12 ? merchant.lastOnlineTime / 1000 : merchant.lastOnlineTime))
        : 9999,
      vipLevel: Number(merchant.badge) ?? 0,
      badge: Number(merchant.badge) ?? 0,
    },
    buyerKycLimit: item.kycLevel ?? undefined,
  };
}

// ─── Normalize advertiser type from MEXC merchantType ───────────────────
function normalizeUserType(raw: string): "user" | "merchant" {
  const upper = raw.toUpperCase();
  return upper === "MERCHANT" || upper === "PRO" ? "merchant" : "user";
}

// ─── Fake ads detection (MEXC-specific heuristics) ──────────────────────
function isFakeAds(ad: P2PAd): boolean {
  const dynamicMaxTrade = ad.dynamicMaxSingleTransAmount;
  const percentSuccess = (ad.advertiser.monthFinishRate ?? 0) * 100;
  const minTrade = ad.minSingleTransAmount;

  if (dynamicMaxTrade <= 5_000_000) return true;
  if (percentSuccess < 90) return true;
  if (minTrade >= 50_000_000 && percentSuccess < 97 && dynamicMaxTrade >= 1_000_000_000) return true;
  if (dynamicMaxTrade / minTrade < 3 && (dynamicMaxTrade < 8_000_000 || percentSuccess < 98)) return true;

  // MEXC — tight range + high price is a classic fake pattern
  if (minTrade < 200_000 && dynamicMaxTrade > 490_000_000 && Number(ad.price) > 27_500) return true;

  return false;
}

// ─── VIP style lookup ───────────────────────────────────────────────────
function getVipStyle(ad: P2PAd, accessLevel: number): string | null {
  for (const rule of VIP_RULES) {
    if (accessLevel < (rule.minAccessLevel ?? 1)) continue;
    if (rule.match(ad)) return rule.className;
  }

  return null;
}

// ─── Price signals (MEXC — same logic as Binance) ────────────────────────
function getPriceSignals(ad: P2PAd, accessLevel: number, ctx?: { isKycRequired?: boolean; hasBankPaymentMethod?: boolean; transAmount?: number }): PriceSignal[] {
  const signals: PriceSignal[] = [];
  const { dynamicMaxSingleTransAmount, tradeType, classify, minSingleTransAmount, maxSingleTransAmount, takerAdditionalKycRequired } = ad;
  const isKyc = ctx?.isKycRequired ?? (takerAdditionalKycRequired === 1);
  const hasBank = ctx?.hasBankPaymentMethod ?? true;
  const transAmt = ctx?.transAmount ?? 0;

  // 🐳 cá mập: SELL 500M-1B
  if (accessLevel >= 1 && dynamicMaxSingleTransAmount >= 500_000_000 && dynamicMaxSingleTransAmount < 1_000_000_000 && tradeType === "SELL") {
    signals.push({ emoji: "🐳", label: "Cá mập (bán)", priceClass: "" });
  }

  // 🐋 cá ông: SELL ≥1B (không block)
  if (accessLevel >= 1 && dynamicMaxSingleTransAmount >= 1_000_000_000 && tradeType === "SELL" && classify !== "block") {
    signals.push({ emoji: "🐋", label: "Cá ông (bán)", priceClass: "" });
  }

  // 🐬 cá heo: BUY ≥1B (không block) — cho free & premium
  if (accessLevel >= 1 && dynamicMaxSingleTransAmount >= 1_000_000_000 && tradeType === "BUY" && classify !== "block") {
    signals.push({ emoji: "🐬", label: "Cá heo (mua)", priceClass: "" });
  }

  // ⚠️ cảnh báo min cao: access 0, SELL, min 99M-700M
  if (accessLevel >= 1 && tradeType === "SELL" && minSingleTransAmount >= 99_000_000 && minSingleTransAmount < 700_000_000) {
    signals.push({ emoji: "⚠️", label: "Min cao (≥99M)", priceClass: "" });
  }

  // 🐲 rồng: BUY, min 19M-60M, max >1B, trans ≥100M — access ≥1
  if (accessLevel >= 1 && tradeType === "BUY" && minSingleTransAmount > 19_000_000 && minSingleTransAmount < 60_000_000 && maxSingleTransAmount > 490_000_000 && transAmt >= 100_000_000) {
    signals.push({ emoji: "🐲", label: "Rồng (mua signaled)", priceClass: "" });
  }

  return signals;
}

// ══════════════════════════════════════════════════════════════════════
//  MEXC Adapter Implementation
// ══════════════════════════════════════════════════════════════════════
export const mexcAdapter: ExchangeAdapter = {
  id: "mexc",
  displayName: "MEXC P2P",
  title: "MEXC P2P",
  favicon: "/mexc-favicon.svg",

  // ─── searchAds ─────────────────────────────────────────────────────
  async searchAds(params: SearchParams, isMobile?: boolean): Promise<SearchResult> {
    try {
      const mobile = isMobile ?? isMobileFromUA();
      const tradeType = params.tradeType === 0 ? "SELL" : "BUY";

      // Build query params per MEXC P2P API docs
      const queryParams = new URLSearchParams({
        allowTrade: "false",
        amount: String(params.transAmount || 1000000),
        blockTrade: "false",
        certifiedMerchant: "false",
        coinId: MEXC_USDT_COIN_ID,
        countryCode: "",
        currency: "VND",
        follow: "false",
        haveTrade: "false",
        page: "1",
        payMethod: "",
        pageSize: "20",
        tradeType,
      });

      const targetUrl = `https://${MEXC_P2P_HOST}/api/market?${queryParams.toString()}`;

      const config = mobile
        ? {
            method: "get" as const,
            url: `${randomHost(MEXC_CORS_PROXIES)}?url=${encodeURIComponent(targetUrl)}`,
            headers: {
              Accept: "application/json, text/plain, */*",
              "Accept-Language": "en-GB,en-US;q=0.9,en;q=0.8",
            },
          }
        : {
            method: "get" as const,
            url: targetUrl,
            headers: {
              Accept: "application/json, text/plain, */*",
              "Accept-Language": "en-GB,en-US;q=0.9,en;q=0.8",
            },
          };

      const res = await axios(config)
        .then((r) => r.data)
        .catch((err) => err?.response?.data || null);

      // MEXC response: { code: 0, message: "success", data: [Offer[]] }
      let adsList: any[] = [];
      if (res?.code === 0 && Array.isArray(res.data)) {
        adsList = res.data;
      }

      const normalized = adsList.map((item: any) => normalizeOffer(item));

      return { data: normalized };
    } catch (error) {
      console.error("[MEXC] searchAds error:", error);
      return { data: [] };
    }
  },

  // ─── getAdDetail ─────────────────────────────────────────────────────
  async getAdDetail(advNo: string, isMobile?: boolean): Promise<P2PAdDetail | null> {
    try {
      // MEXC doesn't have a dedicated ad detail endpoint.
      // We search all ads and find the matching one.
      const mobile = isMobile ?? isMobileFromUA();

      const queryParams = new URLSearchParams({
        allowTrade: "false",
        amount: "10000000",
        blockTrade: "false",
        certifiedMerchant: "false",
        coinId: MEXC_USDT_COIN_ID,
        countryCode: "",
        currency: "VND",
        follow: "false",
        haveTrade: "false",
        page: "1",
        payMethod: "",
        tradeType: "SELL",
      });

      const targetUrl = `https://${MEXC_P2P_HOST}/api/market?${queryParams.toString()}`;

      const config = mobile
        ? {
            method: "get" as const,
            url: `${randomHost(MEXC_CORS_PROXIES)}?url=${encodeURIComponent(targetUrl)}`,
            headers: {
              Accept: "application/json, text/plain, */*",
              "Accept-Language": "en-GB,en-US;q=0.9,en;q=0.8",
            },
          }
        : {
            method: "get" as const,
            url: targetUrl,
            headers: {
              Accept: "application/json, text/plain, */*",
              "Accept-Language": "en-GB,en-US;q=0.9,en;q=0.8",
            },
          };

      const res = await axios(config)
        .then((r) => r.data)
        .catch((err) => err?.response?.data || null);

      if (!res || res.code !== 0 || !Array.isArray(res.data)) return null;

      const found = res.data.find((a: any) => String(a.id) === advNo);
      if (!found) return null;

      const ad = normalizeOffer(found);

      return {
        adv: {
          ...ad,
          initAmount: ad.surplusAmount,
          createTime: 0,
          advUpdateTime: 0,
          advStatus: 1,
          priceType: 0,
          priceFloatingRatio: "",
          rateFloatingRatio: "",
          currencyRate: null,
          fiatUnit: "VND",
          fiatScale: 0,
          priceScale: 0,
          fiatSymbol: "",
          assetLogo: "",
          minSingleTransQuantity: "",
          maxSingleTransQuantity: "",
          dynamicMaxSingleTransQuantity: "",
          allowTradeMerchant: 0,
          buyerRegDaysLimit: -1,
          buyerBtcPositionLimit: -1,
          remarks: "",
          abnormalStatusList: [],
          autoReplyMsg: null,
          takerCommissionRate: null,
          minTakerFee: null,
          isSafePayment: false,
          inventoryType: 0,
          offlineReason: "",
          closeReason: "",
          userTradeCountFilterTime: 0,
          userBuyTradeCountMin: 0,
          userBuyTradeCountMax: 0,
          userSellTradeCountMin: 0,
          userSellTradeCountMax: 0,
          userAllTradeCountMin: 0,
          userAllTradeCountMax: 0,
          userTradeCompleteRateFilterTime: null,
          userTradeCompleteCountMin: 0,
          userTradeCompleteRateMin: "",
          userTradeVolumeFilterTime: 0,
          userTradeType: 0,
          userTradeVolumeMin: "",
          userTradeVolumeMax: "",
          userTradeVolumeAsset: null,
          launchCountry: null,
          tradeMethodCommissionRates: [],
        },
        advertiser: {
          ...ad.advertiser,
          orderCount: 0,
          userStatsRet: {
            registerDays: 0,
            firstOrderDays: 0,
            avgReleaseTimeOfLatest30day: 0,
            avgPayTimeOfLatest30day: 0,
            finishRateLatest30day: ad.advertiser.monthFinishRate,
            completedOrderNumOfLatest30day: 0,
            completedBuyOrderNumOfLatest30day: 0,
            completedSellOrderNumOfLatest30day: 0,
            completedOrderTotalBtcAmountOfLatest30day: 0,
            completedOrderNum: 0,
            completedBuyOrderNum: 0,
            completedSellOrderNum: 0,
            completedBuyOrderTotalBtcAmount: 0,
            completedSellOrderTotalBtcAmount: 0,
            completedOrderTotalBtcAmount: 0,
            counterpartyCount: 0,
          },
          userKycRet: undefined,
          businessStatus: null,
          overComplained: null,
          onlineStatus: null,
          registerDays: null,
          firstOrderDays: null,
          chanelInvolved: "",
          userIdentity: ad.advertiser.userType === "merchant" ? "MERCHANT" : "USER",
          userGradeInstanceRet: null,
          proMerchant: ad.advertiser.userType === "merchant",
          badges: [],
          tradeOrderCount: null,
          isBlocked: null,
          alertEnabled: null,
          lastActiveTime: null,
          companyAccountName: "",
          isCompanyAccount: false,
          eqMargin: 0,
          eqMarginUnit: "",
          marginStats: [],
          avatar: null,
          margin: 0,
          marginUnit: "",
          decimalScale: 0,
          advConfirmTime: 0,
          emailVerified: false,
          bindMobile: false,
          kycVerified: false,
          registrationTime: null,
          payMethodCount: ad.tradeMethods.length,
          mobile: null,
          kycType: 0,
          kycStatus: "",
          userGrade: ad.advertiser.vipLevel,
        },
      };
    } catch {
      return null;
    }
  },

  // ─── isFakeAd ──────────────────────────────────────────────────────
  isFakeAd: (ad: P2PAd) => isFakeAds(ad),

  // ─── getVipStyle ───────────────────────────────────────────────────
  getVipStyle: (ad: P2PAd, accessLevel: number) => getVipStyle(ad, accessLevel),

  // ─── getPriceSignals (MEXC — same logic as Binance) ─────────────────
  getPriceSignals: (ad: P2PAd, accessLevel: number, ctx) => getPriceSignals(ad, accessLevel, ctx),

  // ─── getMerchantColorRules (MEXC — badge-based) ─────────────────
  getMerchantColorRules: (ad: P2PAd) => {
    const badge = ad.advertiser.badge ?? 0;
    return badge > 0 ? "text-cyan-300" : null;
  },

  // ─── isMerchantPerspective (MEXC: merchant perspective) ───────────
  isMerchantPerspective: () => true,

  // ─── getLogo (MEXC — badge-based) ───────────────────────────────
  getLogo: (ad: P2PAd) => {
    const badge = (ad.advertiser as any).badge;
    if (badge === 4) return "💎";      // legacy (whale)
    if (badge === 3) return "👑";      // prime
    if (badge === 1) return "⚜️";      // merchant
    return "🐮";                       // normal user
  },

  // ─── MEXC P2P links (no external linking supported) ──────────────
  getAdvertiserLink: () => null,
  getLink: () => null,
};
